import { Copy, ExternalLink, Link2, Phone, Power, SlidersHorizontal, Trash2 } from 'lucide-react';
import type { AdminClient } from '@/shared/types/client';
import type { BadgeProps } from '@/shared/ui/badge';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/shared/ui/dropdown-menu';
import { EntityListCard, type EntityListCardBadge } from '@/shared/components/entity-list-card';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { PassbookLogoThumb } from '@/shared/components/passbook-logo-thumb';
import { CLIENT_KIND_LABELS, CLIENT_LIFECYCLE_LABELS } from '../api/use-clients';

interface ClientCardProps {
  client: AdminClient;
  index: number;
  onEdit: () => void;
  onInspect?: () => void;
  onToggleActive: () => void;
  onRemove?: () => void;
  onClone?: () => void;
}

function lifecycleVariant(status: AdminClient['lifecycleStatus']): BadgeProps['variant'] {
  return status === 'cliente' ? 'success' : 'warning';
}

function kindVariant(kind: AdminClient['clientKind']): BadgeProps['variant'] {
  if (kind === 'parceiro') return 'accent';
  if (kind === 'whitelabel') return 'info';
  return 'default';
}

function aggregateStats(client: AdminClient) {
  let cadastros = 0;
  let ios = 0;
  let android = 0;
  for (const pb of client.passbooks ?? []) {
    if (pb.onlyShortUrl) continue;
    cadastros += pb.stats?.cadastros ?? 0;
    ios += pb.stats?.ios ?? 0;
    android += pb.stats?.android ?? 0;
  }
  return { cadastros, ios, android };
}

export function ClientCard({
  client,
  index,
  onEdit,
  onInspect,
  onToggleActive,
  onRemove,
  onClone,
}: ClientCardProps) {
  const passbook = client.passbooks?.find((p) => !p.onlyShortUrl) ?? client.passbooks?.[0];
  const shortUrl = passbook?.shortUrl;
  const passbookCount = client.passbooks?.length ?? 0;
  const stats = aggregateStats(client);
  const lifecycle = client.lifecycleStatus ?? 'prospect';
  const kind = client.clientKind ?? 'fixe';

  const badges: EntityListCardBadge[] = [
    ...(client.registrationStatus === 'draft'
      ? [
          {
            key: 'draft',
            label: 'Rascunho',
            variant: 'warning' as const,
            copyLines: [{ label: 'Cadastro', value: 'Enviado pelo formulário público, aguardando aprovação' }],
          },
        ]
      : []),
    {
      key: 'contact',
      label: 'Contato',
      icon: Phone,
      variant: 'info',
      disabled: !client.cnpj && !client.email && !client.phone && !client.site,
      copyLines: [
        ...(client.cnpj ? [{ label: 'CNPJ', value: client.cnpj }] : []),
        ...(client.email ? [{ label: 'E-mail', value: client.email }] : []),
        ...(client.phone ? [{ label: 'Telefone', value: client.phone }] : []),
        ...(client.site ? [{ label: 'Site', value: client.site }] : []),
      ],
    },
    {
      key: 'lifecycle',
      label: CLIENT_LIFECYCLE_LABELS[lifecycle],
      variant: lifecycleVariant(lifecycle),
      copyLines: [
        {
          label: 'Ciclo',
          value: lifecycle === 'cliente' ? 'Cliente ativo na plataforma' : 'Prospect em negociação',
        },
      ],
    },
    {
      key: 'kind',
      label: CLIENT_KIND_LABELS[kind],
      variant: kindVariant(kind),
      copyLines: [
        {
          label: 'Tipo',
          value:
            kind === 'fixe'
              ? 'Operação Fixe padrão'
              : kind === 'parceiro'
                ? 'Cliente parceiro'
                : 'Marca white label',
        },
      ],
    },
    {
      key: 'status',
      label: client.inactive ? 'Inativo' : 'Ativo',
      variant: client.inactive ? 'destructive' : 'success',
      size: 'sm',
      copyLines: [
        { label: 'Status', value: client.inactive ? 'Inativo no sistema' : 'Ativo no sistema' },
        ...(stats.cadastros > 0 ? [{ label: 'Cadastros', value: String(stats.cadastros) }] : []),
        ...(stats.ios > 0 ? [{ label: 'iOS', value: String(stats.ios) }] : []),
        ...(stats.android > 0 ? [{ label: 'Android', value: String(stats.android) }] : []),
      ],
    },
  ];

  return (
    <EntityListCard
      selected={false}
      selectable={false}
      onToggle={() => undefined}
      onCardClick={onEdit}
      index={index}
      ariaLabel={`Configurar cliente ${client.name}`}
      title={client.name || 'Sem nome'}
      thumbnail={
        <PassbookLogoThumb
          passbookId={passbook?._id}
          name={client.name}
          backgroundColor={passbook?.backgroundColor}
        />
      }
      badges={badges}
      actions={
        <ListCardActions
          viewFirst
          viewLabel="Configurar"
          viewIcon={SlidersHorizontal}
          onView={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          menuChildren={
            <>
              {onInspect ? (
                <DropdownMenuItem onSelect={onInspect}>
                  <ExternalLink />
                  Inspecionar painel
                </DropdownMenuItem>
              ) : null}
              {onClone ? (
                <DropdownMenuItem
                  onSelect={onClone}
                  disabled={passbookCount !== 1}
                  title={
                    passbookCount !== 1
                      ? `Clonagem só é suportada para clientes com exatamente 1 passbook (este tem ${passbookCount}).`
                      : undefined
                  }
                >
                  <Copy />
                  Clonar cliente
                </DropdownMenuItem>
              ) : null}
              {shortUrl ? (
                <DropdownMenuItem asChild>
                  <a href={shortUrl} target="_blank" rel="noreferrer">
                    <Link2 />
                    Abrir Fixe.Club
                  </a>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onToggleActive}
                className={client.inactive ? 'text-success focus:text-success' : 'text-warning focus:text-warning'}
              >
                <Power />
                {client.inactive ? 'Reativar cliente' : 'Inativar cliente'}
              </DropdownMenuItem>
              {onRemove ? (
                <DropdownMenuItem onSelect={onRemove} destructive>
                  <Trash2 />
                  Remover cliente
                </DropdownMenuItem>
              ) : null}
            </>
          }
        />
      }
    />
  );
}
